// 🎯 Mis metas (sql/125): cuentas de la meta del mes, sugerencias, ideas de
// Kontrol, el "cómo vas hoy" y los mensajes de ánimo. Funciones puras: se
// prueban sin base de datos.

export const DIAS_SEMANA = [
  { n: 0, corto: 'D', nombre: 'domingo' }, { n: 1, corto: 'L', nombre: 'lunes' }, { n: 2, corto: 'M', nombre: 'martes' },
  { n: 3, corto: 'M', nombre: 'miércoles' }, { n: 4, corto: 'J', nombre: 'jueves' }, { n: 5, corto: 'V', nombre: 'viernes' },
  { n: 6, corto: 'S', nombre: 'sábado' },
];
export const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const fechaYMD = ymd;
export const mesDe = (d = new Date()) => ymd(new Date(d.getFullYear(), d.getMonth(), 1));
export const nombreMes = (mesYMD) => { const [a, m] = mesYMD.split('-').map(Number); return `${MESES[m - 1]} ${a}`; };
export const mesAnterior = (mesYMD, n = 1) => { const [a, m] = mesYMD.split('-').map(Number); return ymd(new Date(a, m - 1 - n, 1)); };
const inicioDia = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

export function diasDelMes(mesYMD) {
  const [a, m] = mesYMD.split('-').map(Number);
  const dias = [];
  for (let d = new Date(a, m - 1, 1); d.getMonth() === m - 1; d = new Date(a, m - 1, d.getDate() + 1)) dias.push(d);
  return dias;
}

// Redondeo "bonito" para metas y presupuestos sugeridos.
export function redondear(n) {
  const v = Number(n) || 0;
  if (v >= 10000) return Math.round(v / 500) * 500;
  if (v >= 1000) return Math.round(v / 100) * 100;
  return Math.round(v / 50) * 50;
}

// Cómo va la meta: cuánto debería llevar a hoy, cuánto falta por día, etc.
export function calcularAvance({ meta, vendido, mes, diasVenta, hoy = new Date() }) {
  const dias = diasDelMes(mes);
  const habiles = (diasVenta && diasVenta.length) ? dias.filter((d) => diasVenta.includes(d.getDay())) : dias;
  const total = habiles.length || dias.length;
  const h = inicioDia(hoy);
  const transcurridos = habiles.filter((d) => d < h).length;
  const restantes = habiles.filter((d) => d >= h).length;
  const m = Number(meta) || 0; const v = Number(vendido) || 0;
  const porDia = total ? m / total : 0;
  const esperado = porDia * transcurridos;
  const falta = Math.max(0, m - v);
  const porDiaRestante = restantes ? falta / restantes : falta;
  const pct = m > 0 ? Math.round((v / m) * 100) : 0;
  let estado = 'en_ritmo';
  if (m > 0 && v >= m) estado = 'cumplida';
  else if (transcurridos === 0) estado = 'empezando';
  else if (v >= esperado) estado = 'adelantada';
  else if (v >= esperado * 0.85) estado = 'en_ritmo';
  else estado = 'atrasada';
  return {
    meta: m, vendido: v, pct, falta, esperado, diferencia: v - esperado,
    porDia, porSemana: porDia * ((diasVenta && diasVenta.length) || 7), porDiaRestante,
    diasVentaTotal: total, transcurridos, restantes, hoyVende: habiles.some((d) => +d === +h), estado,
  };
}

// Meta sugerida: promedio de los últimos meses con ventas + 15 %.
export function sugerirMeta(ventasPorMes) {
  const conVentas = (ventasPorMes || []).filter((x) => x.total > 0).slice(0, 3);
  if (!conVentas.length) return null;
  const prom = conVentas.reduce((s, x) => s + x.total, 0) / conVentas.length;
  return { meta: redondear(prom * 1.15), promedio: prom, meses: conVentas.length };
}

// Presupuesto sugerido por categoría: promedio de lo gastado en los meses anteriores.
export function sugerirPresupuesto(gastos, mesesAnteriores) {
  const set = new Set(mesesAnteriores);
  const suma = {};
  (gastos || []).forEach((g) => {
    const mes = `${String(g.fecha).slice(0, 7)}-01`;
    if (!set.has(mes)) return;
    const c = g.categoria || 'Otros';
    suma[c] = (suma[c] || 0) + Number(g.monto || 0);
  });
  const n = Math.max(1, mesesAnteriores.length);
  return Object.fromEntries(Object.entries(suma).map(([c, t]) => [c, redondear(t / n)]));
}

// Cómo va cada categoría de gasto contra su presupuesto.
export function estadoPresupuesto(presupuesto, gastadoPorCategoria) {
  const cats = new Set([...Object.keys(presupuesto || {}), ...Object.keys(gastadoPorCategoria || {})]);
  return [...cats].map((c) => {
    const p = Number((presupuesto || {})[c] || 0); const g = Number((gastadoPorCategoria || {})[c] || 0);
    const pct = p > 0 ? Math.round((g / p) * 100) : null;
    const estado = p <= 0 ? (g > 0 ? 'sin_presupuesto' : 'ok') : pct > 100 ? 'pasado' : pct >= 80 ? 'cuidado' : 'ok';
    return { categoria: c, presupuesto: p, gastado: g, pct, estado };
  }).sort((a, b) => (b.pct ?? -1) - (a.pct ?? -1) || b.gastado - a.gastado);
}

// Día de la semana en que más vende (en el histórico que se le pase).
export function mejorDiaSemana(ventasPorFecha) {
  const t = Array(7).fill(0);
  Object.entries(ventasPorFecha || {}).forEach(([f, v]) => { const [a, m, d] = f.split('-').map(Number); t[new Date(a, m - 1, d).getDay()] += v; });
  const max = Math.max(...t);
  return max > 0 ? DIAS_SEMANA[t.indexOf(max)] : null;
}

// Días seguidos sin vender hasta ayer (solo días de venta).
export function diasSinVender(ventasPorFecha, diasVenta, hoy = new Date()) {
  let n = 0;
  for (let i = 1; i <= 30; i += 1) {
    const d = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - i);
    if (diasVenta && diasVenta.length && !diasVenta.includes(d.getDay())) continue;
    if ((ventasPorFecha || {})[ymd(d)] > 0) break;
    n += 1;
  }
  return n;
}

const dineroCon = (s) => (n) => `${s} ${Math.round(Number(n || 0)).toLocaleString('es-HN')}`;

// 💡 Ideas de Kontrol: estrategias armadas con los números del negocio.
export function ideasKontrol(d, simbolo = 'L') {
  const dinero = dineroCon(simbolo);
  const ideas = [];
  const add = (clave, icono, titulo, detalle) => ideas.push({ clave, icono, titulo, detalle });
  if (d.sinVentas) {
    add('primeras', '🚀', 'Consigue tus primeras 10 ventas', 'Comparte tu catálogo en línea con 20 contactos de WhatsApp y publica 3 productos en tus estados hoy mismo.');
    add('productos', '📦', 'Sube tus 10 productos estrella con foto', 'Con fotos claras y precio, tus clientas pueden pedir solas desde el catálogo.');
  }
  if (d.avance && d.avance.estado === 'atrasada') {
    add('recuperar', '⚡', `Recupera ${dinero(-d.avance.diferencia)} con una oferta corta`, 'Una promo de 48 horas (envío gratis o 2x1 en algo que tengas de más) en tus redes y WhatsApp.');
  }
  if (d.clientesSinVolver > 0) {
    add('recompra', '💌', `Escríbeles a tus ${d.clientesSinVolver} clientas que no han vuelto`, 'Usa la Campaña de recompra en Clientes: les llega su nombre, su talla y tu catálogo.');
  }
  // Solo los 3 tipos que más se venden (y con ventas de verdad) cuentan como "estrella".
  const estrellaAgotandose = (d.topProductos || []).slice(0, 3).find((p) => p.unidades >= 3 && p.stock !== null && p.stock <= 2);
  if (estrellaAgotandose) {
    add('reponer', '📦', `Repón ${estrellaAgotandose.nombre}`, `Es de lo que más vendes (${estrellaAgotandose.unidades} vendidos) y te quedan ${estrellaAgotandose.stock}.`);
  }
  if (d.mejorDia) {
    add('mejor-dia', '📅', `Prepara una promo para los ${d.mejorDia.nombre}`, `Es tu día más fuerte: publica la noche antes y ten listo lo que más se vende.`);
  }
  if (d.ticketProm > 0 && d.ticketPromAnterior > 0 && d.ticketProm < d.ticketPromAnterior * 0.9) {
    add('combos', '🛍️', 'Sube tu venta promedio con combos', `Este mes cada venta es de ${dinero(d.ticketProm)} y antes era de ${dinero(d.ticketPromAnterior)}. Ofrece "lleva 2" o un complemento.`);
  }
  (d.presupuesto || []).filter((r) => r.estado === 'pasado').slice(0, 1).forEach((r) => {
    add(`gasto-${r.categoria}`, '💸', `Controla tus gastos de ${r.categoria}`, `Vas en ${dinero(r.gastado)} de ${dinero(r.presupuesto)} (${r.pct}%). Revisa qué se puede posponer.`);
  });
  if (d.pedidosPendientes > 0) {
    add('pedidos', '🛒', `Atiende tus ${d.pedidosPendientes} pedidos del catálogo`, 'Son ventas casi hechas: confírmalos hoy por WhatsApp.');
  }
  add('redes', '📣', 'Publica 3 veces por semana', 'Usa "Publicar en redes" desde tu catálogo: precio, talla y foto en un toque.');
  return ideas;
}

// Cómo vas hoy: lo bueno, lo que necesita atención y la idea del día (máx. 5).
export function coachDelDia(d, simbolo = 'L') {
  const dinero = dineroCon(simbolo);
  const out = [];
  const a = d.avance;
  if (a && a.meta > 0) {
    if (a.estado === 'cumplida') out.push({ tono: 'bien', icono: '🏆', texto: `¡Cumpliste tu meta del mes! Llevas ${dinero(a.vendido)}. Todo lo que vendas ahora es extra.` });
    else if (a.estado === 'adelantada') out.push({ tono: 'bien', icono: '🚀', texto: `Vas adelantada: llevas ${dinero(a.vendido)} y a hoy necesitabas ${dinero(a.esperado)}.` });
    else if (a.estado === 'en_ritmo') out.push({ tono: 'bien', icono: '👍', texto: `Vas en ritmo. Para llegar, vende ${dinero(a.porDiaRestante)} en cada uno de los ${a.restantes} días que quedan.` });
    else if (a.estado === 'atrasada') out.push({ tono: 'alerta', icono: '⏰', texto: `Vas ${dinero(-a.diferencia)} atrás de tu ritmo. Te toca vender ${dinero(a.porDiaRestante)} por día los ${a.restantes} días que quedan.` });
    else out.push({ tono: 'idea', icono: '🌱', texto: `Empieza el mes: tu meta diaria es de ${dinero(a.porDia)}.` });
  }
  if (d.vendidoAyer != null && d.promedioDiario > 0) {
    if (d.vendidoAyer >= d.promedioDiario * 1.3) out.push({ tono: 'bien', icono: '🔥', texto: `Ayer vendiste ${dinero(d.vendidoAyer)}, más que tu día normal (${dinero(d.promedioDiario)}). ¿Qué funcionó? Repítelo.` });
    else if (d.vendidoAyer > 0 && d.vendidoAyer < d.promedioDiario * 0.5) out.push({ tono: 'atencion', icono: '📉', texto: `Ayer vendiste ${dinero(d.vendidoAyer)}, menos de la mitad de tu día normal. Hoy publica algo nuevo.` });
  }
  if (d.diasSinVender >= 2) out.push({ tono: 'alerta', icono: '🚨', texto: `Llevas ${d.diasSinVender} días de venta sin vender nada. Escríbeles a tus clientas o comparte tu catálogo hoy.` });
  (d.presupuesto || []).filter((r) => r.estado === 'pasado' || r.estado === 'cuidado').slice(0, 2).forEach((r) => {
    out.push({ tono: r.estado === 'pasado' ? 'alerta' : 'atencion', icono: '💸', texto: `${r.categoria}: llevas ${dinero(r.gastado)} de ${dinero(r.presupuesto)} (${r.pct}%).` });
  });
  const f = d.finanzas;
  if (f?.numero && a?.meta > 0 && a.meta < f.numero.ventas * 0.97) out.push({ tono: 'atencion', icono: '🧘', texto: `Para cubrir gastos y deudas necesitas vender ${dinero(f.numero.ventas)} al mes y tu meta es ${dinero(a.meta)}. Mira 💰 Finanzas.` });
  if (d.pendientesHoy > 0) out.push({ tono: 'idea', icono: '📝', texto: `Tienes ${d.pendientesHoy} pendiente(s) para hoy.` });
  if (d.pedidosPendientes > 0) out.push({ tono: 'atencion', icono: '🛒', texto: `${d.pedidosPendientes} pedido(s) del catálogo esperan tu respuesta.` });
  return out.slice(0, 5);
}

// Mensajes de ánimo: uno distinto cada día, según cómo va.
const ANIMO = {
  empezando: ['Mes nuevo, oportunidades nuevas. ¡A darle! 🌱', 'Hoy pones la primera piedra de tu meta 💪', 'Un buen mes empieza con un buen primer día ✨'],
  adelantada: ['¡Vas volando! Sigue así 🚀', 'Tu esfuerzo se nota en los números 🌟', 'Vas adelante: aprovecha para probar algo nuevo 💡'],
  en_ritmo: ['Paso a paso también se llega lejos 👣', 'La constancia le gana al talento. ¡Sigue! 🔥', 'Vas bien: no aflojes ahora 💪'],
  atrasada: ['Un mal día no define tu mes. ¡Hoy se recupera! 💪', 'Todavía hay tiempo: una buena idea cambia todo ✨', 'Cada venta cuenta. Empieza por una 🎯'],
  cumplida: ['¡Lo lograste! Celebra y ve por más 🏆', 'Meta cumplida: eres la prueba de que sí se puede 🎉', 'Tu negocio creció gracias a ti 💖'],
};
export function fraseAnimo(estado, fecha = new Date()) {
  const lista = ANIMO[estado] || ANIMO.en_ritmo;
  const dia = Math.floor(inicioDia(fecha).getTime() / 86400000);
  return lista[dia % lista.length];
}

// Ventas acumuladas día a día contra la línea ideal (para la gráfica).
export function acumulado(ventasPorFecha, mes, meta, diasVenta, hoy = new Date()) {
  const dias = diasDelMes(mes);
  const habiles = dias.filter((d) => !diasVenta || !diasVenta.length || diasVenta.includes(d.getDay())).length || dias.length;
  let real = 0; let hechos = 0;
  const h = inicioDia(hoy);
  return dias.map((d) => {
    if (!diasVenta || !diasVenta.length || diasVenta.includes(d.getDay())) hechos += 1;
    const futuro = d > h;
    if (!futuro) real += Number((ventasPorFecha || {})[ymd(d)] || 0);
    return { dia: d.getDate(), fecha: ymd(d), real: futuro ? null : real, ideal: (Number(meta) || 0) * (hechos / habiles) };
  });
}
